import { useState, useRef, useEffect } from "react";
import {
  ChevronDown,
  Pencil,
  ShieldCheck,
  LogOut,
} from "lucide-react";
import type { Artist } from "../data";

interface NavbarProps {
  artists: Artist[];
  email: string;
  isAdmin: boolean;
  isAdminWorkspace: boolean;
  logout: () => void;
  selectedArtist: Artist | undefined;
  switchToAdminWorkspace: () => void;
  switchToArtistWorkspace: (id: string) => void;
  onEditArtistProfile: (id: string) => void;
}

export default function Navbar({
  artists,
  email,
  isAdmin,
  isAdminWorkspace,
  logout,
  selectedArtist,
  switchToAdminWorkspace,
  switchToArtistWorkspace,
  onEditArtistProfile,
}: NavbarProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const ADMIN_DISPLAY: Record<string, string> = {
    "benjaminj27@live.com.au": "BJ",
    "johnsonboibeats@gmail.com": "BJ",
  };
  const dropdownName = isAdminWorkspace ? "Admin" : (selectedArtist?.name ?? "Johnsonboibeats");
  const welcomeName = isAdminWorkspace
    ? (ADMIN_DISPLAY[email?.toLowerCase() ?? ""] ?? "Admin")
    : (selectedArtist?.name ?? "Johnsonboibeats");

  useEffect(() => {
    function onClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, []);

  return (
    <nav className="navbar-glass">
      <div className="brand-switcher" ref={ref}>
        {isAdmin && artists.length > 0 ? (
          <>
            <button
              aria-expanded={open}
              className="brand-button"
              onClick={() => setOpen((v) => !v)}
              type="button"
            >
              <span>{dropdownName}</span>
              <ChevronDown className={`h-4 w-4 ${open ? "rotate-180" : ""}`} />
            </button>
            {open ? (
              <div className="artist-switcher-menu">
                <button
                  className={`switcher-item ${isAdminWorkspace ? "active" : ""}`}
                  onClick={() => {
                    switchToAdminWorkspace();
                    setOpen(false);
                  }}
                  type="button"
                >
                  <span className="switcher-label">
                    <ShieldCheck className="h-4 w-4" />
                    Admin
                  </span>
                </button>
                <div className="switcher-divider" />
                {artists.map((a) => (
                  <button
                    key={a.id}
                    className={`switcher-item ${!isAdminWorkspace && a.id === selectedArtist?.id ? "active" : ""}`}
                    onClick={() => {
                      switchToArtistWorkspace(a.id);
                      setOpen(false);
                    }}
                    type="button"
                  >
                    {a.name}
                  </button>
                ))}
                {selectedArtist ? (
                  <>
                    <div className="switcher-divider" />
                    <button
                      aria-label={`Edit ${selectedArtist.name} profile`}
                      className="switcher-item"
                      onClick={() => {
                        setOpen(false);
                        onEditArtistProfile(selectedArtist.id);
                      }}
                      type="button"
                    >
                      <Pencil className="h-4 w-4" />
                      Edit profile
                    </button>
                  </>
                ) : null}
              </div>
            ) : null}
          </>
        ) : (
          <span className="brand-static">
            {selectedArtist?.name ?? "Johnsonboibeats"}
          </span>
        )}
      </div>
      <div className="navbar-actions">
        {email ? (
          <span className="welcome-text">Welcome, {welcomeName}</span>
        ) : null}
        <span className="admin-badge">
          <ShieldCheck className="h-3.5 w-3.5" />
          {isAdminWorkspace ? "Admin" : "Artist"}
        </span>
        <button className="logout-button" onClick={() => void logout()} type="button">
          <LogOut className="h-4 w-4" />
          Sign out
        </button>
      </div>
    </nav>
  );
}
